import { Button, cn, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, Spinner } from '@ds';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { CaseStatusChangeModals } from '@lp/features/CaseStatusActions/components/CaseStatusChangeModals';
import { useCaseStatusMenuState } from '@lp/features/CaseStatusActions/hooks/useCaseStatusMenuState';
import { CaseStatus } from '@/api/apis/case';
import type { Case } from '@/api/apis/case';
import { getCaseStatusName, getColorByStatus, hidden } from '@/utils/helpers';
import type { Uuid } from '@/api/types';
import { caseStatusNames } from '@/constants/case/case.constants';
import { useCaseByIdQuery } from '@/queries/case';
import { ActionHintIcon } from '../Tabs/AssignedResources/components/ActionHintIcon';

export const CaseStatusMenu = ({ caseId }: { caseId: Uuid }) => {
  const { data: caseData } = useCaseByIdQuery(caseId);
  const { status, randomization } = caseData as Case;

  const {
    hasPermission,
    isDirectAcquisitionCase,
    proceedHidden,
    nextStatus,
    prevStatus,
    canMoveToPrevStatus,
    canChangeStatus,
    canDeclineCase,
    canDeclineService,
    isProceeding,
    proceedDisabled,
    proceedInteractionDisabled,
    disabledKeys,
    actionHintMessage,
    updateStatus,
    onProceedClick,
    onOcs,
    offOcs,
    declineCase,
    declineService,
  } = useCaseStatusMenuState(caseData as Case);

  const isCaseFinished = status === CaseStatus.Finished;

  return (
    <>
      <Dropdown placement="bottom-start" isDisabled={isProceeding || !canChangeStatus || !hasPermission}>
        <DropdownTrigger>
          <Button
            disableRipple
            size="md"
            className={`py-0 ps-4 pe-3 h-8 rounded-full ${getColorByStatus(status)}`}
            endContent={isProceeding ? <Spinner size="sm" /> : <MdKeyboardArrowDown size={20} />}
          >
            {getCaseStatusName(status, randomization?.result)}
          </Button>
        </DropdownTrigger>

        <DropdownMenu
          aria-label="Case actions"
          itemClasses={{ title: ['text-m-r!'] }}
          selectionMode="none"
          disabledKeys={disabledKeys}
        >
          <DropdownItem
            key="proceedStatus"
            textValue="proceedStatus"
            aria-label="Proceed"
            className={hidden(proceedHidden)}
            onClick={onProceedClick}
            endContent={
              <div className="pointer-events-auto">
                <ActionHintIcon show={proceedDisabled} message={actionHintMessage} data-testid="proceed-hint" />
              </div>
            }
          >
            <div
              className={cn({
                'opacity-50 cursor-not-allowed': proceedInteractionDisabled,
              })}
            >
              {`Proceed to ${
                isDirectAcquisitionCase
                  ? caseStatusNames[CaseStatus.Closed]
                  : getCaseStatusName(nextStatus, randomization?.result)
              }`}
            </div>
          </DropdownItem>

          <DropdownItem
            key="rollBackStatus"
            textValue="rollBackStatus"
            aria-label="Rollback"
            className={hidden(!canMoveToPrevStatus || isDirectAcquisitionCase || isCaseFinished)}
            onClick={() => canMoveToPrevStatus && updateStatus(caseId, prevStatus)}
          >
            {`Roll back to ${getCaseStatusName(prevStatus, randomization?.result)}`}
          </DropdownItem>

          <DropdownItem
            key="declineCase"
            textValue="declineCase"
            aria-label="DeclineCase"
            className={hidden(!canDeclineCase || isDirectAcquisitionCase)}
            onClick={canDeclineCase ? declineCase.onOpen : undefined}
          >
            <span className="text-danger-30">Decline Case</span>
          </DropdownItem>

          <DropdownItem
            key="declineService"
            textValue="declineService"
            aria-label="DeclineService"
            className={hidden(!canDeclineService || isDirectAcquisitionCase)}
            onClick={canDeclineService ? declineService.onOpen : undefined}
          >
            <span className="text-danger-30">Decline Service</span>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      <CaseStatusChangeModals
        caseData={caseData as Case}
        declineCase={declineCase}
        declineService={declineService}
        onOcs={onOcs}
        offOcs={offOcs}
      />
    </>
  );
};
